import React, { useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import LogDateField from './LogDateField';
import LogTimeField from './LogTimeField';
import LogDatePickerPopover from './LogDatePickerPopover';

const formatDisplayDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const formatTimeValue = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '00:00';
  }

  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const LogDateTimeFields = ({
  value,
  onChange,
  dateLabel = 'Date',
  timeLabel = 'Time',
  allowFuture = false,
  disableFuture = true,
  sx = {},
  dateFieldSx = {},
  timeFieldSx = {},
}) => {
  const currentValue = useMemo(
    () => (value instanceof Date && !Number.isNaN(value.getTime()) ? value : new Date()),
    [value]
  );
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);

  const handleDatePickerOpen = (event) => {
    setDatePickerAnchor(event.currentTarget);
  };

  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
  };

  const handleDateChange = (nextDate) => {
    if (!nextDate || Number.isNaN(nextDate.getTime())) {
      return;
    }

    const nextValue = new Date(currentValue);
    nextValue.setFullYear(nextDate.getFullYear(), nextDate.getMonth(), nextDate.getDate());
    onChange?.(nextValue);
    setDatePickerAnchor(null);
  };

  const handleTimeChange = (event) => {
    const [hours, minutes] = String(event.target.value || '00:00')
      .split(':')
      .map((part) => Number(part));

    const nextValue = new Date(currentValue);
    nextValue.setHours(
      Number.isFinite(hours) ? hours : 0,
      Number.isFinite(minutes) ? minutes : 0,
      0,
      0
    );
    onChange?.(nextValue);
  };

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      <Stack direction="row" spacing={1} sx={{ width: '100%', alignItems: 'stretch' }}>
        <Box sx={{ flex: 1.05, minWidth: 0 }}>
          <LogDateField
            label={dateLabel}
            value={formatDisplayDate(currentValue)}
            onClick={handleDatePickerOpen}
            sx={dateFieldSx}
          />
        </Box>

        <Box sx={{ flex: 0.95, minWidth: 0 }}>
          <LogTimeField
            label={timeLabel}
            value={formatTimeValue(currentValue)}
            onChange={handleTimeChange}
            sx={timeFieldSx}
          />
        </Box>
      </Stack>

      <LogDatePickerPopover
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={handleDatePickerClose}
        value={currentValue}
        onChange={handleDateChange}
        allowFuture={allowFuture}
        disableFuture={disableFuture}
      />
    </Box>
  );
};

export default LogDateTimeFields;
